<script module>
	import { tick } from "svelte";

	export function useValidations() {
		let errors = $state({});

		function validateForm(schema, data) {
			resetErrors();
			return schema
				.validate(data, { abortEarly: false })
				.catch((validationError) => {
					validationError.inner.forEach((error) => {
						setError(error.path, error.errors);
					});
				});
		}

		function validateField(schema, fieldString, data) {
			removeError(fieldString);
			return schema
				.validateAt(fieldString, data, { abortEarly: false })
				.catch((validationError) => {
					validationError.inner.forEach((error) => {
						setError(error.path, error.errors);
					});
				});
		}

		function setError(field, errorMessages) {
			errors[field] = errorMessages;
		}

		function removeError(field) {
			delete errors[field];
		}

		function resetErrors() {
			errors = {};
		}

		function getErrors() {
			return errors;
		}

		return {
			removeError,
			resetErrors,
			setError,
			validateField,
			validateForm,
			getErrors,
		};
	}

	/**
	 * A reusable Svelte action for validating form inputs.
	 * It listens for `input` events and sets a custom validity message.
	 * Usage: add use:validity={validationMethod} on the input tag
	 * @param {HTMLInputElement} input The input element to validate.
	 * @param {(value: string) => string | null | undefined} validator A function that returns an error message or an empty string.
	 */
	export function validity(input, validator) {
		function validate() {
			// Call the validator function and set the message.
			// An empty string means the element is valid.
			const message = validator(input.value) ?? "";
			input.setCustomValidity(message);
		}

		// Run validation on initial mount and whenever the input changes.
		validate();
		input.addEventListener("input", validate);

		return {
			destroy() {
				// Clean up the event listener when the element is removed.
				input.removeEventListener("input", validate);
			},
		};
	}

	export function maskNumericValue(input, inputType, mask, value) {
		function maskValue(value) {
			let masked = "";
			let i = makeDigiter(value);
			if (!i.more) {
				return masked;
			}

			for (const m of mask) {
				if (m === "9" && i.next()) {
					// consume digit
					masked += i.value;
				} else if (i.more) {
					// use mask character
					masked += m;
				} else {
					// ran out of digits
					break;
				}
			}
			return masked;
		}

		function makeDigiter(value) {
			const digits = getDigits(value);
			const { length } = digits;
			let i = 0;

			return {
				more: i < length,
				value: null,
				next() {
					if (this.more) {
						this.value = digits[i];
						i += 1;
						this.more = i < length;
						return true;
					} else {
						return false;
					}
				},
			};
		}

		function getDigits(value) {
			let digits = "";
			for (const c of value) {
				if (c >= "0" && c <= "9") {
					digits += c;
				}
			}
			return digits;
		}

		async function queueRestoreSelection(input, start, end) {
			await tick();
			input.setSelectionRange(start, end);
		}

		function isRightOfMask(position) {
			return position > 1 && mask[position - 1] !== "9";
		}

		function fixIt(input, state, sanitized) {
			if (state === sanitized && sanitized !== input.value) {
				// Because 'SvelteDOMSetProperty' isn't fired
				input.value = sanitized;
			}
			return sanitized;
		}

		const {
			selectionStart: oldStart,
			selectionEnd: oldEnd,
			value: { length: oldLength },
		} = input;
		let [start, end] = [oldStart, oldEnd];

		value = fixIt(input, value, maskValue(input.value));

		const { length } = value;
		switch (inputType) {
			case "insertText":
				const adjust = length - oldLength;
				if (adjust > 0) {
					start += adjust;
				} else if (isRightOfMask(start)) {
					start += 1;
				}
				break;

			case "deleteContentBackward":
				if (isRightOfMask(start)) {
					// Immediately to the right of non-digit
					// character, move to right of next digit
					// to the left.
					// (TODO: more than one adjacent mask character)
					start -= 1;
				}
				break;

			case "deleteContentForward":
				if (start === length && isRightOfMask(start)) {
					// End of content, immediately to the right of non-digit
					// character, move to right of last digit
					// (TODO: more than one adjacent mask character)
					start -= 1;
				}
				break;
		}

		if (oldStart === oldEnd) {
			end = start;
		}
		queueRestoreSelection(input, start, end);
		return value;
	}
</script>
